import { useCallback, useEffect, useRef, useState } from 'react';
import { AiStreamChunk, readSseChunks } from './sse';
import { isHiddenTool, toolDisplayName } from './transcript';

/**
 * One live AI turn over SSE (rev33 "Event contract (as built)" + rev 3.3 §5 / §10.3):
 *   phase{phase,sinceMs,pod?,detail?} · thinking{text?,thinkingTokens?} · tool_call{toolName,toolUseId?,input?}
 *   · tool_done{toolUseId,isError,bytes} · text{content} · done (once) · error{error,code?}.
 *
 * The state always names the LATEST chunk — never a browser guess. The browser owns
 * only `sending` (the request is in flight, no chunk yet) and the liveness clock
 * (`lastByteAt`, which the keep-alive `: ping` frames also move).
 *
 * - `steps` is ONE ordered list: narration (text written before a tool call),
 *   summarized thinking, tools. Order is kept exactly as the chunks came.
 * - A named `tool_call` closes the current text: it becomes a narration step and
 *   `textSoFar` starts over. The durable answer — btgpt's and the builder's alike
 *   (both sinks save the run's final `result`) — is the text after the last tool,
 *   so `textSoFar` at `done` equals the saved reply on every surface. A bare
 *   `tool_call` (btgpt reconcile) discards the text. A repeated `tool_call` with a
 *   known `toolUseId` only adds its `input` (never a new step).
 * - `tool_done` ends its tool step (ok / error, size, duration). Unknown ids — a
 *   hidden tool's — are ignored.
 * - `error{code:'pending'}` is not a failure: the turn is past the time limit and its
 *   answer will be saved (`phase: 'pending'`; `onError` still reports it).
 * - The hook records everything; what a surface shows is its detail level (the
 *   render filters — Quiet included, which still needs the phases).
 * - `done` is honoured once; its `content` (btgpt) is the authoritative answer.
 * - `reset()` / unmount abort the fetch; nothing is reported after that.
 */

export type AiStreamPhase =
  /** Nothing running. */
  | 'idle'
  /** The request is in flight and no chunk has arrived yet (the one FE-owned phase). */
  | 'sending'
  /** The latest chunk was a backend `phase` (`infra` says which). */
  | 'infra'
  | 'thinking'
  | 'tool'
  | 'streaming'
  /** `error{code:'pending'}`: past the time limit, the answer lands in the conversation. */
  | 'pending'
  | 'done'
  | 'error';

/** The latest backend step (`phase` chunk). */
export interface AiInfraPhase {
  phase: string;
  pod?: string;
  detail?: string;
}

/** A visible tool the turn used, by raw name (format with `toolDisplayName`). */
export interface AiStreamTool {
  id: string;
  name: string;
}

export type AiStreamStep =
  | { kind: 'narration'; id: string; text: string }
  | { kind: 'thinking'; id: string; text: string; startedAt: number; endedAt?: number }
  | {
      kind: 'tool';
      id: string;
      toolUseId?: string;
      name: string;
      /** The n-th call of this tool in the turn ("call 3"). Never "3 of N": the total does not exist ahead of time. */
      call: number;
      input?: string;
      startedAt: number;
      endedAt?: number;
      isError?: boolean;
      bytes?: number;
    };

/**
 * The Technical level's log: every backend phase, and every tool (its step holds the
 * details). `at` = when it happened (a phase: arrival − `sinceMs`); `arrivedAt` = when
 * its chunk reached the browser (the status line's pacing plays entries in arrival order).
 */
export type AiStreamLogEntry =
  | { id: string; at: number; arrivedAt: number; kind: 'phase'; phase: string; pod?: string; detail?: string }
  | { id: string; at: number; arrivedAt: number; kind: 'tool'; stepId: string };

export interface AiStreamError {
  /** `server`: an `error` chunk; `http`: a non-2xx answer; `transport`: network failure or the stream ended with no `done`. */
  kind: 'server' | 'http' | 'transport';
  /** The server's message (an `error` chunk, or the JSON `error`/`message` of an HTTP failure). Absent for transport failures. */
  message?: string;
  status?: number;
  /** A server error's machine code, when it sends one (`pending` = still working, the answer will be saved). */
  code?: string;
}

export interface AiStreamState {
  phase: AiStreamPhase;
  /** The latest backend phase while `phase === 'infra'`; kept through `done` only when it was `saving`. */
  infra: AiInfraPhase | null;
  /** The answer so far (the text since the last tool call). */
  textSoFar: string;
  steps: AiStreamStep[];
  log: AiStreamLogEntry[];
  error: AiStreamError | null;
  /** When the turn started (ms epoch, browser clock). */
  startedAt: number | null;
  /** When the current step started: the chunk's arrival, minus its `sinceMs` for a backend phase. */
  stepStartedAt: number | null;
  /** The phase the live view was in when it failed (`phase: 'error'`): its label stays until the 35 s liveness rule. */
  failedFrom: AiStreamPhase | null;
  /** When the last byte arrived — a chunk OR a keep-alive ping ("Connection quiet" after 35 s without one). */
  lastByteAt: number | null;
  /** The backend's token estimate for the current thinking block (`thinking.thinkingTokens`); null = none sent. */
  thinkingTokens: number | null;
}

/** A URL (GET, or `init`) or a function that performs the request itself. */
export type AiStreamSource = string | ((signal: AbortSignal) => Promise<Response>);

export interface UseAiStreamOptions {
  /** Request options for a URL source (e.g. auth headers). */
  init?: RequestInit | (() => RequestInit);
  /** Every chunk, including surface-specific extras (btgpt `settings-picker`, a bare `tool_call`). */
  onChunk?: (chunk: AiStreamChunk) => void;
  /** The turn finished: the final text, the visible tools, and the `done` chunk itself. */
  onDone?: (result: { text: string; tools: AiStreamTool[]; chunk: AiStreamChunk }) => void;
  /** `partial`: the answer text received before the failure, '' if none. Also called for `pending`. */
  onError?: (error: AiStreamError, partial: string) => void;
  /**
   * The user (on any tab) pressed Stop: a terminal `stopped` chunk — no `done`,
   * no `error` follows. Not a failure: finalize the turn neutrally ("Stopped."),
   * same shape as `onDone`.
   */
  onStopped?: (result: { text: string; tools: AiStreamTool[]; chunk: AiStreamChunk }) => void;
}

export interface UseAiStream extends AiStreamState {
  start: (source?: AiStreamSource) => Promise<void>;
  /** Back to idle (e.g. once the surface has moved the finished turn into its durable items). */
  reset: () => void;
}

export const AI_STREAM_IDLE: AiStreamState = {
  phase: 'idle',
  infra: null,
  textSoFar: '',
  steps: [],
  log: [],
  error: null,
  startedAt: null,
  stepStartedAt: null,
  failedFrom: null,
  lastByteAt: null,
  thinkingTokens: null,
};

/** The phases a turn is running in (the status line shows). */
export const AI_STREAM_RUNNING: ReadonlySet<AiStreamPhase> = new Set<AiStreamPhase>([
  'sending',
  'infra',
  'thinking',
  'tool',
  'streaming',
  'pending',
]);

/** The visible tools of a turn, in call order. */
export function stepTools(steps: AiStreamStep[]): AiStreamTool[] {
  return steps.flatMap((s) => (s.kind === 'tool' ? [{ id: s.id, name: s.name }] : []));
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** Close an open thinking step (the next step has started). */
function closeThinking(steps: AiStreamStep[], at: number): AiStreamStep[] {
  const last = steps[steps.length - 1];
  return last?.kind === 'thinking' && last.endedAt === undefined ? [...steps.slice(0, -1), { ...last, endedAt: at }] : steps;
}

/**
 * The pure chunk → state step (everything but `done` / `error` / `text` buffering,
 * which need the request's own variables). Exported for the reducer tests.
 */
export function applyChunk(s: AiStreamState, chunk: AiStreamChunk, at: number): AiStreamState {
  const next: AiStreamState = { ...s, lastByteAt: at };
  // Any step but thinking ends the thinking block's count.
  const leave: AiStreamState = { ...next, thinkingTokens: null };
  switch (chunk.type) {
    case 'phase': {
      const phase = str(chunk.phase);
      if (!phase) return next;
      const infra: AiInfraPhase = { phase, pod: str(chunk.pod), detail: str(chunk.detail) };
      const since = typeof chunk.sinceMs === 'number' && chunk.sinceMs > 0 ? chunk.sinceMs : 0;
      const stepStartedAt = at - since;
      const same = s.phase === 'infra' && s.infra?.phase === infra.phase && s.infra.detail === infra.detail && s.infra.pod === infra.pod;
      return {
        ...leave,
        phase: 'infra',
        infra,
        steps: closeThinking(s.steps, at),
        stepStartedAt,
        // A stream that attached late (a builder reconnect) learns the turn began earlier.
        startedAt: s.startedAt === null ? stepStartedAt : Math.min(s.startedAt, stepStartedAt),
        log: same ? s.log : [...s.log, { id: `p${s.log.length}`, at: stepStartedAt, arrivedAt: at, kind: 'phase', ...infra }],
      };
    }
    case 'thinking': {
      const text = typeof chunk.text === 'string' ? chunk.text : '';
      const tokens = typeof chunk.thinkingTokens === 'number' && chunk.thinkingTokens >= 0 ? chunk.thinkingTokens : null;
      const last = s.steps[s.steps.length - 1];
      const open = last?.kind === 'thinking' && last.endedAt === undefined ? last : null;
      // A bare chunk (no text, no count) = a new thinking block started.
      const newBlock = !text && tokens === null;
      let steps = s.steps;
      if (text && open) steps = [...steps.slice(0, -1), { ...open, text: open.text + text }];
      else if (text) steps = [...steps, { kind: 'thinking', id: `s${steps.length}`, text, startedAt: at }];
      else if (newBlock) steps = closeThinking(steps, at);
      const wasThinking = s.phase === 'thinking';
      return {
        ...next,
        phase: 'thinking',
        infra: null,
        steps,
        stepStartedAt: wasThinking ? s.stepStartedAt : at,
        thinkingTokens: tokens !== null ? tokens : newBlock || !wasThinking ? null : s.thinkingTokens,
      };
    }
    case 'tool_call': {
      const name = str(chunk.toolName) ?? '';
      const toolUseId = str(chunk.toolUseId);
      const input = str(chunk.input);
      // The same tool use again: its arguments arrived. Add them; nothing moves.
      const known = toolUseId ? s.steps.findIndex((x) => x.kind === 'tool' && x.toolUseId === toolUseId) : -1;
      if (known >= 0) {
        const steps = [...s.steps];
        const step = steps[known] as Extract<AiStreamStep, { kind: 'tool' }>;
        steps[known] = { ...step, input: input ?? step.input };
        return { ...next, steps };
      }
      let steps = closeThinking(s.steps, at);
      const narration = name ? s.textSoFar.trim() : '';
      if (narration) steps = [...steps, { kind: 'narration', id: `s${steps.length}`, text: narration }];
      // A btgpt reconcile (no name) or a hidden tool (ToolSearch) is no step of its own:
      // the text resets, the phase (and its label, from the last real chunk) stays.
      if (!name || isHiddenTool(name)) return { ...next, steps, textSoFar: '' };
      const display = toolDisplayName(name);
      const call = s.steps.filter((x) => x.kind === 'tool' && toolDisplayName(x.name) === display).length + 1;
      const id = `s${steps.length}`;
      steps = [...steps, { kind: 'tool', id, toolUseId, name, call, input, startedAt: at }];
      return {
        ...leave,
        phase: 'tool',
        infra: null,
        textSoFar: '',
        steps,
        stepStartedAt: at,
        log: [...s.log, { id: `l${s.log.length}`, at, arrivedAt: at, kind: 'tool', stepId: id }],
      };
    }
    case 'tool_done': {
      const toolUseId = str(chunk.toolUseId);
      const i = toolUseId ? s.steps.findIndex((x) => x.kind === 'tool' && x.toolUseId === toolUseId) : -1;
      if (i < 0) return next; // a hidden tool (never listed), or an id we never saw
      const steps = [...s.steps];
      const step = steps[i] as Extract<AiStreamStep, { kind: 'tool' }>;
      steps[i] = {
        ...step,
        endedAt: at,
        isError: chunk.isError === true,
        bytes: typeof chunk.bytes === 'number' ? chunk.bytes : undefined,
      };
      // The latest chunk is this tool's end — also for one of several parallel calls
      // ("Checked … ✓", then the status falls back to a call still running).
      const latest = i === steps.map((x) => x.kind).lastIndexOf('tool');
      return latest || s.phase === 'tool' ? { ...leave, steps, phase: 'tool', infra: null, stepStartedAt: at } : { ...next, steps };
    }
    default:
      return next;
  }
}

async function httpError(res: Response): Promise<AiStreamError> {
  let message: string | undefined;
  try {
    const body = await res.json();
    message = body?.error || body?.message || undefined;
  } catch {
    /* no JSON body */
  }
  return { kind: 'http', status: res.status, message };
}

export function useAiStream(source?: AiStreamSource, options: UseAiStreamOptions = {}): UseAiStream {
  const [state, setState] = useState<AiStreamState>(AI_STREAM_IDLE);
  const opts = useRef(options);
  opts.current = options;
  const defaultSource = useRef(source);
  defaultSource.current = source;
  const controller = useRef<AbortController | null>(null);
  const latest = useRef<AiStreamState>(AI_STREAM_IDLE);

  const reset = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    latest.current = AI_STREAM_IDLE;
    setState(AI_STREAM_IDLE);
  }, []);

  useEffect(() => () => controller.current?.abort(), []);

  const start = useCallback(async (override?: AiStreamSource) => {
    const src = override ?? defaultSource.current;
    if (!src) return;
    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;
    const alive = () => controller.current === ctrl && !ctrl.signal.aborted;
    // One source of truth for this request: every update goes through here.
    const update = (fn: (s: AiStreamState) => AiStreamState) => {
      if (!alive()) return;
      latest.current = fn(latest.current);
      setState(latest.current);
    };

    const now = Date.now();
    latest.current = { ...AI_STREAM_IDLE, phase: 'sending', startedAt: now, stepStartedAt: now, lastByteAt: now };
    setState(latest.current);

    let finished = false;

    const fail = (error: AiStreamError) => {
      finished = true;
      const partial = latest.current.textSoFar;
      const pending = error.code === 'pending';
      // A failure is not a byte: liveness keeps counting from the last real one.
      update((s) =>
        pending
          ? { ...s, phase: 'pending', infra: null, error, stepStartedAt: Date.now() }
          : { ...s, phase: 'error', failedFrom: s.phase, error }
      );
      opts.current.onError?.(error, partial);
    };

    try {
      const res =
        typeof src === 'string'
          ? await fetch(src, {
              ...(typeof opts.current.init === 'function' ? opts.current.init() : opts.current.init),
              signal: ctrl.signal,
            })
          : await src(ctrl.signal);
      if (!alive()) return;
      if (!res.ok || !res.body) {
        const err = await httpError(res);
        if (alive()) fail(err);
        return;
      }
      update((s) => ({ ...s, lastByteAt: Date.now() })); // the response headers are bytes too

      const keepAlive = () => update((s) => ({ ...s, lastByteAt: Date.now() }));
      for await (const chunk of readSseChunks(res.body, keepAlive)) {
        if (!alive()) return;
        const at = Date.now();
        opts.current.onChunk?.(chunk);

        if (chunk.type === 'text') {
          if (typeof chunk.content !== 'string' || !chunk.content) {
            update((s) => ({ ...s, lastByteAt: at }));
            continue;
          }
          const content = chunk.content;
          update((s) => ({
            ...s,
            phase: 'streaming',
            infra: null,
            textSoFar: s.textSoFar + content,
            steps: closeThinking(s.steps, at),
            stepStartedAt: s.phase === 'streaming' ? s.stepStartedAt : at,
            lastByteAt: at,
            thinkingTokens: null,
          }));
        } else if (chunk.type === 'done') {
          finished = true;
          const text = latest.current.textSoFar;
          const final = typeof chunk.content === 'string' && chunk.content.trim() ? chunk.content : text;
          update((s) => ({
            ...s,
            phase: 'done',
            // "Saving…" stays readable while a surface waits for the saved turn.
            infra: s.phase === 'infra' && s.infra?.phase === 'saving' ? s.infra : null,
            textSoFar: final,
            steps: closeThinking(s.steps, at),
            lastByteAt: at,
            thinkingTokens: null,
          }));
          opts.current.onDone?.({ text: final, tools: stepTools(latest.current.steps), chunk });
          break;
        } else if (chunk.type === 'stopped') {
          finished = true;
          const text = latest.current.textSoFar;
          const final = typeof chunk.content === 'string' && chunk.content.trim() ? chunk.content : text;
          update((s) => ({
            ...s,
            phase: 'done',
            infra: null,
            textSoFar: final,
            steps: closeThinking(s.steps, at),
            lastByteAt: at,
            thinkingTokens: null,
          }));
          opts.current.onStopped?.({ text: final, tools: stepTools(latest.current.steps), chunk });
          break;
        } else if (chunk.type === 'error') {
          fail({
            kind: 'server',
            message: typeof chunk.error === 'string' ? chunk.error : undefined,
            code: typeof chunk.code === 'string' ? chunk.code : undefined,
          });
          break;
        } else {
          update((s) => applyChunk(s, chunk, at));
        }
      }
      if (!finished && alive()) fail({ kind: 'transport' });
    } catch {
      if (alive() && !finished) fail({ kind: 'transport' });
    } finally {
      if (controller.current === ctrl) controller.current = null;
    }
  }, []);

  return { ...state, start, reset };
}
